import * as pdfjsLib from "pdfjs-dist/build/pdf";

// Worker desde CDN matcheando la versión instalada (mismo patrón que el visor del proyecto).
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

/**
 * Parser del SWIFT (PDF con XML pacs.008.001.08 embebido como texto) → campos del Paso 2 del
 * cobro export. 100% en cliente, sin subir el archivo. Dos capas (el texto extraído del PDF viene
 * sucio —pdfjs inyecta espacios/cortes dentro de los tags—):
 *   1. DOMParser sobre el bloque <Document> aislado (camino limpio).
 *   2. Fallback regex tolerante por campo si el DOMParser falla por el whitespace.
 * Devuelve { instdAmt, citibank, recibidoSantander, uetr, ordenante, bic, fechaValor } o lanza
 * Error si no se reconoce ningún monto clave (PDF equivocado / formato distinto).
 */

const aNumero = (s) => {
  if (s == null) return null;
  const n = parseFloat(String(s).replace(/[^\d.]/g, ""));
  return Number.isNaN(n) ? null : n;
};

// ─── Extracción de texto del PDF ─────────────────────────────────────────────
export const extraerTextoPdf = async (file) => {
  const buffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
  let texto = "";
  for (let i = 1; i <= pdf.numPages; i++) {
    // eslint-disable-next-line no-await-in-loop
    const page = await pdf.getPage(i);
    // eslint-disable-next-line no-await-in-loop
    const content = await page.getTextContent();
    texto += `${content.items.map((it) => it.str).join(" ")}\n`;
  }
  return texto;
};

// ─── Capa 1: DOMParser (namespace-agnostic vía getElementsByTagNameNS) ────────
const parseConDom = (texto) => {
  const bloque = texto.match(/<document[\S\s]*<\/document>/i);
  if (!bloque) return null;
  let doc;
  try {
    doc = new DOMParser().parseFromString(bloque[0], "text/xml");
  } catch {
    return null;
  }
  if (doc.getElementsByTagName("parsererror").length > 0) return null;

  const txt = (el) => el?.textContent?.trim() ?? null;
  const first = (root, name) => root?.getElementsByTagNameNS("*", name)?.[0] ?? null;
  const firstTxt = (name) => txt(first(doc, name));

  const instd = firstTxt("InstdAmt");
  const sttlm = firstTxt("IntrBkSttlmAmt");
  if (instd == null && sttlm == null) return null; // no es el XML esperado

  const chrgs = first(doc, "ChrgsInf");
  const dbtr = first(doc, "Dbtr");
  const dbtrAgt = first(doc, "DbtrAgt");
  return {
    instdAmt: aNumero(instd),
    citibank: aNumero(txt(first(chrgs, "Amt"))),
    recibidoSantander: aNumero(sttlm),
    uetr: firstTxt("UETR"),
    ordenante: txt(first(dbtr, "Nm")),
    bic: txt(first(dbtrAgt, "BICFI")),
    fechaValor: firstTxt("IntrBkSttlmDt"),
  };
};

// ─── Capa 2: regex tolerante al whitespace que mete el PDF ────────────────────
const parseConRegex = (texto) => {
  const grab = (re) => {
    const m = texto.match(re);
    return m ? m[1].trim() : null;
  };
  const instd = aNumero(grab(/instdamt[^>]*>\s*([\d,.]+)/i));
  const sttlm = aNumero(grab(/intrbksttlmamt[^>]*>\s*([\d,.]+)/i));
  if (instd == null && sttlm == null) return null;
  return {
    instdAmt: instd,
    // Amt dentro del bloque ChrgsInf (no confundir con InstdAmt/IntrBkSttlmAmt, que son tags propios).
    citibank: aNumero(grab(/chrgsinf[\S\s]*?amt[^>]*>\s*([\d,.]+)/i)),
    recibidoSantander: sttlm,
    uetr: grab(/uetr\s*>\s*([\da-f-]{20,})/i),
    // <Dbtr> (la parte ordenante) — el ancla `Dbtr\s*>` NO matchea <DbtrAgt> (lleva "Agt" antes del >).
    ordenante: grab(/<\s*dbtr\s*>[\S\s]*?nm\s*>\s*([^<]+?)\s*</i),
    bic: grab(/bicfi[^>]*>\s*([\da-z]{8,11})/i),
    fechaValor: grab(/intrbksttlmdt[^>]*>\s*(\d{4}-\d{2}-\d{2})/i),
  };
};

export const parsearSwiftDesdePdf = async (file) => {
  const texto = await extraerTextoPdf(file);
  const r = parseConDom(texto) || parseConRegex(texto);
  // Exigir al menos un monto clave: si no hay InstdAmt ni IntrBkSttlmAmt, no es un SWIFT legible.
  if (!r || (r.instdAmt == null && r.recibidoSantander == null)) {
    throw new Error("No se pudo leer el SWIFT");
  }
  return r;
};
